<template>
  <div v-if="!pending">
    <h2 class="text-2xl my-8 md:mb-6 md:mt-2 md:block md:text-left innerTransition__title">
      {{ serviceTitleText }}
      <button v-show="services.length > 1" class="inline md:hidden" @click="isServiceModalOpen = true">
        <NuxtImg loading="lazy" src="/icons/c-info-e.svg" alt="info" class="" />
      </button>
      <ProductInfoServiceModal :is-open="isServiceModalOpen" @on-modal-close="isServiceModalOpen = false" />
    </h2>

    <div
      v-if="selectionStep === 'service_selection'"
      class="bg-white divide-y-2 rounded-lg shadow-default overflow-hidden innerTransition__container"
    >
      <div v-for="(serviceList, index) in services" :key="index">
        <CommonServiceTypeBlock @onClicked="handleSelectService" :serviceList="serviceList" />
      </div>
    </div>
    <div :key="selectedServiceSlug">
      <GetStartedSectionsTopicsClinicTest v-if="selectedServiceSlug === 'clinic-test'" />
      <GetStartedSectionsTopicsEverywhereNurseService v-if="selectedServiceSlug === 'everywhere-nurse-service'" />
      <GetStartedSectionsTopicsInstantTest v-if="selectedServiceSlug === 'instant-test'" />
    </div>
  </div>

  <div v-else class="w-full h-96 flex justify-center items-center">
    <NuxtImg class="w-20" src="/icons/loading.svg" />
  </div>
</template>

<script setup lang="ts">
import type { GetStartedService } from "../types";

const { t } = useI18n();
const route = useRoute();
const getStartedModalStore = useGetStartedModalStore();
const pending = ref(true);
const services = ref<GetStartedService[]>([]);

onMounted(async () => {
  const servicesResult = await useServiceListType();
  services.value = servicesResult.data.value?.data ?? [];
  pending.value = false;
});

/**
 * List of availables services.
 */
const ALLOWED_SERVICES = ["clinic-test", "everywhere-nurse-service", "instant-test"] as const;
type ServiceSlug = (typeof ALLOWED_SERVICES)[number];

/**
 * Currently focused section.
 */
const selectedSectionSlug = computed(() => route.params.section);

/**
 * Currently focused service.
 */
const selectedServiceSlug = computed(() => route.params.service as ServiceSlug);

/**
 * Finds the selected service entity from the service list array depending on the route params.
 */
const selectedService = computed(() => services.value.find((service) => service.slug === selectedServiceSlug.value));

/**
 * Set selected service
 */
watch(selectedService, getStartedModalStore.setSelectedService, {
  immediate: true,
});

/**
 * @param service - The service to generate a route for.
 * @returns A route object for `navigateTo`.
 */
function getServiceRoute(service: GetStartedService) {
  const locale = switchBlogLocale();
  const { slug } = service;

  if (slug === "home-test-kit") {
    return {
      path: `/${locale}/tests/sexual-health`,
      query: { services: slug },
    };
  }

  return {
    path: `/${locale}/get-started/${selectedSectionSlug.value}/${slug}`,
  };
}

/**
 * @param service - Target service
 */
async function handleSelectService(service: GetStartedService) {
  const route = getServiceRoute(service);
  return navigateTo(route);
}

/**
 * Handle service modal state.
 */
const isServiceModalOpen = ref<boolean>(false);

/**
 * Computed value for service heading
 */
const serviceTitleText = computed(() => {
  switch (true) {
    case Boolean(selectedServiceSlug.value === "clinic-test"):
      return "";

    case Boolean(selectedServiceSlug.value === "everywhere-nurse-service"):
      return t("getStarted.everywhereNurseService.serviceTitle");

    case Boolean(selectedServiceSlug.value):
      return t("getStarted.serviceTypeSelection.chooseTopic");

    default:
      return t("getStarted.serviceTypeSelection.default");
  }
});

/**
 * The current selection step of the flow.
 */
const selectionStep = computed<"service_selection" | "topic_selection">(() =>
  route.params.service ? "topic_selection" : "service_selection"
);
</script>
